import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AuditLogRow {
  id: string;
  action: string;
  description: string | null;
  metadata: any;
  created_at: string;
  actor_user_id: string;
  organization_id: string | null;
  actor_name?: string;
}

export interface AuditLogFilters {
  action?: string;          // 'all' or action key
  actorUserId?: string;     // 'all' or user id
  organizationId?: string;  // 'all' or org id
  search?: string;          // text in description
  dateFrom?: Date;
  dateTo?: Date;
}

interface Params {
  page: number;            // 1-based
  pageSize: number;
  filters?: AuditLogFilters;
  enabled?: boolean;
}

export function useAuditLogs({ page, pageSize, filters, enabled = true }: Params) {
  return useQuery({
    queryKey: ['audit-logs-paged', page, pageSize, filters],
    queryFn: async (): Promise<{ logs: AuditLogRow[]; total: number }> => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let q = supabase
        .from('audit_logs')
        .select('id, action, description, metadata, created_at, actor_user_id, organization_id', {
          count: 'exact',
        })
        .order('created_at', { ascending: false })
        .order('id'); // desempate estable para que el paginado de UI no baraje filas en los bordes

      if (filters?.action && filters.action !== 'all') q = q.eq('action', filters.action);
      if (filters?.actorUserId && filters.actorUserId !== 'all') q = q.eq('actor_user_id', filters.actorUserId);
      if (filters?.organizationId && filters.organizationId !== 'all') q = q.eq('organization_id', filters.organizationId);
      if (filters?.dateFrom) q = q.gte('created_at', new Date(filters.dateFrom.setHours(0, 0, 0, 0)).toISOString());
      if (filters?.dateTo) q = q.lte('created_at', new Date(filters.dateTo.setHours(23, 59, 59, 999)).toISOString());
      if (filters?.search && filters.search.trim()) {
        q = q.ilike('description', `%${filters.search.trim()}%`);
      }

      const { data, error, count } = await q.range(from, to);
      if (error) throw error;

      const rows = data || [];
      const actorIds = [...new Set(rows.map((l) => l.actor_user_id))];
      let actorMap = new Map<string, string>();
      if (actorIds.length > 0) {
        const { data: actors } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', actorIds);
        actorMap = new Map((actors || []).map((a: any) => [a.id, a.full_name]));
      }

      const logs: AuditLogRow[] = rows.map((l) => ({
        ...l,
        actor_name: actorMap.get(l.actor_user_id) || 'Desconocido',
      }));

      return { logs, total: count ?? logs.length };
    },
    enabled,
    placeholderData: (prev) => prev,
  });
}
